import React, { useState, useEffect } from 'react';
import {
  Card,
  CardContent,
  Typography,
  Box,
  IconButton,
  Chip,
  Divider,
  Stack,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  InputAdornment,
  Tooltip,
  CircularProgress,
  Alert,
} from '@mui/material';
import {
  Download as DownloadIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Close as CloseIcon,
  Undo as UndoIcon,
  WarningAmber as WarningIcon,
  Add as AddIcon,
  Autorenew as RescanIcon,
  Link as LinkIcon,
  LinkOff as UnlinkIcon,
} from '@mui/icons-material';
import config from '../config';

interface ReceiptItem {
  id: string;
  description: string;
  cost: string;
  order: number;
  included: boolean;
}

interface Receipt {
  id: string;
  invoice_date: string;
  contractor_name: string;
  property_address: string;
  normalized_address: string;
  flat_label?: string;
  pdf_filename: string;
  original_filename: string;
  items: ReceiptItem[];
  created_at: string;
  total: string;
  items_sum?: string;
  items_total_mismatch?: boolean;
  bank_transaction_id?: string | null;
  bank_statement_transaction_id?: string | null;
  confirmed?: boolean;
  excluded_from_transaction_list?: boolean;
}

interface ReceiptCardProps {
  invoice: Receipt;
  onUpdate: () => void;
  onDelete: (invoiceId: string) => void;
  /** Opens the "is this a new expense, or proof of an existing transaction?" dialog for this
   * receipt — only relevant while confirmed === false (see Invoice.confirmed). */
  onReview?: (invoice: Receipt) => void;
}

const ReceiptCard: React.FC<ReceiptCardProps> = ({ invoice, onUpdate, onDelete, onReview }) => {
  const [items, setItems] = useState<ReceiptItem[]>(invoice.items);
  const [itemsDialogOpen, setItemsDialogOpen] = useState(false);
  const [editingField, setEditingField] = useState<{ itemId: string; field: 'description' | 'cost' } | null>(null);
  const [editFieldValue, setEditFieldValue] = useState('');
  const [editOpen, setEditOpen] = useState(false);
  const [editData, setEditData] = useState({
    invoice_date: invoice.invoice_date,
    contractor_name: invoice.contractor_name,
    property_address: invoice.property_address || '',
    normalized_address: invoice.normalized_address || '',
    flat_label: invoice.flat_label || '',
  });
  const [editingTitle, setEditingTitle] = useState(false);
  const [titleDraft, setTitleDraft] = useState(invoice.contractor_name);
  const [newItemDescription, setNewItemDescription] = useState('');
  const [newItemCost, setNewItemCost] = useState('');
  const [addingItem, setAddingItem] = useState(false);
  const [rescanning, setRescanning] = useState(false);
  const [rescanError, setRescanError] = useState<string | null>(null);
  const [unlinkProofConfirmOpen, setUnlinkProofConfirmOpen] = useState(false);
  const [unlinkingProof, setUnlinkingProof] = useState(false);
  const [unlinkProofError, setUnlinkProofError] = useState<string | null>(null);

  // Keep local item state (used for instant total recalculation) in sync when the parent refetches.
  useEffect(() => { setItems(invoice.items); }, [invoice.items]);
  useEffect(() => { setTitleDraft(invoice.contractor_name); }, [invoice.contractor_name]);

  // The extracted total (what's actually printed on the receipt) is authoritative — excluding an
  // item subtracts its own cost from that total rather than re-summing what's left, so one
  // misread item price doesn't silently change the baseline the total starts from. Mirrors
  // invoice_effective_total() in models.py — keep both in sync if this changes.
  const excludedSum = items.filter(i => !i.included).reduce((sum, i) => sum + (parseFloat(i.cost) || 0), 0);
  const total = (parseFloat(invoice.total) || 0) - excludedSum;
  const allItemsSum = items.reduce((sum, i) => sum + (parseFloat(i.cost) || 0), 0);
  const itemsMismatch = items.length > 0 && Math.abs(allItemsSum - (parseFloat(invoice.total) || 0)) > 0.01;

  const handleView = () => {
    window.open(`${config.apiUrl}/api/invoice-view/${invoice.id}`, '_blank');
  };

  const handleDownload = async () => {
    try {
      const token = localStorage.getItem('auth_token');
      const response = await fetch(`${config.apiUrl}/api/invoices/${invoice.id}/download`, {
        headers: { 'Authorization': `Bearer ${token}` },
      });
      if (response.ok) {
        const blob = await response.blob();
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = invoice.original_filename || invoice.pdf_filename;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        window.URL.revokeObjectURL(url);
      }
    } catch (error) {
      console.error('Error downloading receipt:', error);
    }
  };

  const handleToggleItem = async (item: ReceiptItem) => {
    const newIncluded = !item.included;
    setItems(prev => prev.map(i => (i.id === item.id ? { ...i, included: newIncluded } : i)));
    try {
      const token = localStorage.getItem('auth_token');
      const res = await fetch(`${config.apiUrl}/api/invoice-items/${item.id}`, {
        method: 'PATCH',
        headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ included: newIncluded }),
      });
      if (!res.ok) throw new Error('toggle failed');
      // This card's own total is already updated optimistically above, but the flat/property
      // header totals and the page's TOTAL SPENT/NET KPI cards live in the parent — nudge it
      // to refresh (silently) so they don't go stale until some unrelated action refetches.
      onUpdate();
    } catch (error) {
      // revert on failure
      setItems(prev => prev.map(i => (i.id === item.id ? { ...i, included: item.included } : i)));
    }
  };

  const startEditField = (item: ReceiptItem, field: 'description' | 'cost') => {
    setEditingField({ itemId: item.id, field });
    setEditFieldValue(field === 'cost' ? parseFloat(item.cost).toFixed(2) : item.description);
  };

  const cancelEditField = () => setEditingField(null);

  const commitEditField = async () => {
    if (!editingField) return;
    const { itemId, field } = editingField;
    const original = items.find(i => i.id === itemId);
    const trimmed = editFieldValue.trim();
    setEditingField(null);
    if (!original || !trimmed) return; // OCR-wrong is fine, blank is not — just drop the edit

    if (field === 'cost' && isNaN(parseFloat(trimmed))) return;
    if (trimmed === (field === 'cost' ? parseFloat(original.cost).toFixed(2) : original.description)) return;

    setItems(prev => prev.map(i => (i.id === itemId ? { ...i, [field]: trimmed } : i)));
    try {
      const token = localStorage.getItem('auth_token');
      const res = await fetch(`${config.apiUrl}/api/invoice-items/${itemId}`, {
        method: 'PATCH',
        headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ [field]: trimmed }),
      });
      if (!res.ok) throw new Error('update failed');
      const data = await res.json();
      setItems(prev => prev.map(i => (i.id === itemId ? { ...i, ...data.item } : i)));
      onUpdate(); // cost changes affect the card/flat/property totals and the page KPI cards
    } catch (error) {
      setItems(prev => prev.map(i => (i.id === itemId ? original : i)));
    }
  };

  const handleAddItem = async () => {
    const description = newItemDescription.trim();
    const cost = parseFloat(newItemCost);
    if (!description || isNaN(cost)) return;
    setAddingItem(true);
    try {
      const token = localStorage.getItem('auth_token');
      const res = await fetch(`${config.apiUrl}/api/invoices/${invoice.id}/items`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ description, cost: newItemCost }),
      });
      if (!res.ok) throw new Error('add failed');
      const data = await res.json();
      setItems(data.invoice.items);
      setNewItemDescription('');
      setNewItemCost('');
      onUpdate();
    } catch (error) {
      // leave the form filled in so the user can retry
    } finally {
      setAddingItem(false);
    }
  };

  // Re-runs OCR + Gemini extraction on the already-uploaded photo — a fix for a total/items
  // mismatch when it's the AI's read that's wrong, as an alternative to manually correcting/adding
  // items. This REPLACES merchant/date/total and every line item with the fresh extraction.
  const handleRescan = async () => {
    setRescanning(true);
    setRescanError(null);
    try {
      const token = localStorage.getItem('auth_token');
      const res = await fetch(`${config.apiUrl}/api/invoices/${invoice.id}/rescan`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` },
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Re-scan failed');
      setItems(data.invoice.items);
      onUpdate();
    } catch (error: any) {
      setRescanError(error.message || 'Re-scan failed');
    } finally {
      setRescanning(false);
    }
  };

  const handleSaveEdit = async () => {
    try {
      const token = localStorage.getItem('auth_token');
      const response = await fetch(`${config.apiUrl}/api/invoices/${invoice.id}`, {
        method: 'PUT',
        headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify(editData),
      });
      if (response.ok) {
        setEditOpen(false);
        onUpdate();
      }
    } catch (error) {
      console.error('Error updating receipt:', error);
    }
  };

  const commitTitleEdit = async () => {
    const trimmed = titleDraft.trim();
    setEditingTitle(false);
    if (!trimmed || trimmed === invoice.contractor_name) {
      setTitleDraft(invoice.contractor_name);
      return;
    }
    try {
      const token = localStorage.getItem('auth_token');
      const res = await fetch(`${config.apiUrl}/api/invoices/${invoice.id}`, {
        method: 'PUT',
        headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ contractor_name: trimmed }),
      });
      if (res.ok) onUpdate();
      else setTitleDraft(invoice.contractor_name);
    } catch (error) {
      setTitleDraft(invoice.contractor_name);
    }
  };

  // Re-includes this "cash" entry in the merged bank "View transactions" dialog after the user
  // previously removed it from there (see BankConnectionDetailDialog's onRemoveCash) — doesn't
  // touch anything else about the invoice/receipt itself.
  const handleShowInTransactions = async () => {
    const token = localStorage.getItem('auth_token');
    await fetch(`${config.apiUrl}/api/invoices/${invoice.id}/exclude-from-transactions`, {
      method: 'PATCH',
      headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
      body: JSON.stringify({ excluded: false }),
    });
    onUpdate();
  };

  // The opposite of handleShowInTransactions above — hides this "cash" entry from the merged
  // bank "View transactions" list without deleting anything, reversible via "Add to transactions".
  // Only relevant for a confirmed, unlinked receipt that's currently showing there by default.
  const handleHideFromTransactions = async () => {
    const token = localStorage.getItem('auth_token');
    await fetch(`${config.apiUrl}/api/invoices/${invoice.id}/exclude-from-transactions`, {
      method: 'PATCH',
      headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
      body: JSON.stringify({ excluded: true }),
    });
    onUpdate();
  };

  // Unlinking a receipt attached as proof of a real bank transaction is destructive — see
  // ProofViewDialog's own handleUnlink (2026-08-12): it permanently deletes the receipt itself
  // (row, items, stored file), not just the link, so re-attaching the same file elsewhere never
  // hits a stale content_hash "already uploaded" 409. Mirrored here so this action is reachable
  // directly from the Receipts grid, not only from inside the bank transaction's own dialog.
  const handleUnlinkProof = async () => {
    setUnlinkingProof(true);
    setUnlinkProofError(null);
    try {
      const token = localStorage.getItem('auth_token');
      const res = await fetch(`${config.apiUrl}/api/invoices/${invoice.id}/unlink-proof`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` },
      });
      if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        throw new Error(data.error || `Failed to unlink this receipt (${res.status})`);
      }
      setUnlinkProofConfirmOpen(false);
      onUpdate();
    } catch (err: any) {
      setUnlinkProofError(err.message || 'Failed to unlink this receipt');
    } finally {
      setUnlinkingProof(false);
    }
  };

  const formatDate = (dateStr: string) => {
    if (!dateStr || dateStr.includes('not_found')) return 'Date not found';
    try {
      return new Date(dateStr).toLocaleDateString('en-GB');
    } catch {
      return dateStr;
    }
  };

  const formatCurrency = (value: number) => `£${value.toFixed(2)}`;

  return (
    <>
      <Card sx={{ boxShadow: 1, border: '1px solid', borderColor: 'divider', '&:hover': { boxShadow: 3 }, transition: 'box-shadow 0.3s', height: '100%' }}>
        <CardContent sx={{ p: 1.75 }}>
          <Box display="flex" gap={1.75}>
            {/* Left: preview + actions — sized down (30% on mobile, 20% on desktop): it was eating too much space */}
            <Box sx={{ width: { xs: 84, sm: 96 }, flexShrink: 0 }}>
              <Box
                onClick={(e) => { e.preventDefault(); e.stopPropagation(); handleView(); }}
                sx={{
                  width: { xs: 84, sm: 96 }, height: { xs: 106, sm: 122 }, border: '1px solid', borderColor: 'divider', borderRadius: 1,
                  overflow: 'hidden', mb: 1, position: 'relative', cursor: 'pointer',
                  '&:hover': { opacity: 0.8, borderColor: 'primary.main' },
                }}
              >
                <img
                  src={`${config.apiUrl}/api/invoices/${invoice.id}/preview?t=${invoice.created_at}`}
                  alt="Receipt preview"
                  style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                  onError={(e) => { (e.target as HTMLImageElement).style.display = 'none'; }}
                />
              </Box>
              {/* 10% smaller on mobile */}
              <Box display="flex" justifyContent="center" gap={1}>
                <Tooltip title="Edit receipt">
                  <IconButton
                    onClick={(e) => { e.preventDefault(); e.stopPropagation(); setEditOpen(true); }}
                    size="small"
                    sx={{ backgroundColor: 'primary.main', color: 'white', '&:hover': { backgroundColor: 'primary.dark' }, transform: { xs: 'scale(0.9)', sm: 'none' } }}
                  >
                    <EditIcon sx={{ fontSize: 16 }} />
                  </IconButton>
                </Tooltip>
                <Tooltip title="Download image">
                  <IconButton
                    onClick={(e) => { e.preventDefault(); e.stopPropagation(); handleDownload(); }}
                    size="small"
                    sx={{ backgroundColor: 'success.main', color: 'white', '&:hover': { backgroundColor: 'success.dark' }, transform: { xs: 'scale(0.9)', sm: 'none' } }}
                  >
                    <DownloadIcon sx={{ fontSize: 16 }} />
                  </IconButton>
                </Tooltip>
                <Tooltip title="Delete receipt">
                  <IconButton
                    onClick={(e) => { e.preventDefault(); e.stopPropagation(); onDelete(invoice.id); }}
                    size="small"
                    sx={{ backgroundColor: 'error.main', color: 'white', '&:hover': { backgroundColor: 'error.dark' }, transform: { xs: 'scale(0.9)', sm: 'none' } }}
                  >
                    <DeleteIcon sx={{ fontSize: 16 }} />
                  </IconButton>
                </Tooltip>
              </Box>
            </Box>

            {/* Right: details */}
            <Box sx={{ flex: 1, minWidth: 0 }}>
              <Box sx={{ mb: 1 }}>
                {editingTitle ? (
                  <TextField
                    size="small"
                    autoFocus
                    fullWidth
                    value={titleDraft}
                    onChange={(e) => setTitleDraft(e.target.value)}
                    onBlur={commitTitleEdit}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') commitTitleEdit();
                      if (e.key === 'Escape') { setEditingTitle(false); setTitleDraft(invoice.contractor_name); }
                    }}
                    inputProps={{ style: { fontWeight: 600, fontSize: '1.1rem' } }}
                  />
                ) : (
                  <Tooltip title="Click to edit">
                    <Typography
                      variant="h6"
                      noWrap
                      onClick={() => setEditingTitle(true)}
                      sx={{
                        fontWeight: 600, fontSize: '1.1rem', color: 'text.primary',
                        cursor: 'pointer', '&:hover': { textDecoration: 'underline' },
                      }}
                    >
                      {invoice.contractor_name}
                    </Typography>
                  </Tooltip>
                )}
                <Typography variant="caption" sx={{ color: 'text.secondary', fontSize: '0.75rem' }}>
                  {formatDate(invoice.invoice_date)}
                </Typography>
              </Box>

              {/* No "Receipt" type chip here — redundant now that Receipts/Invoices are separate
                  page sections. Always rendered (not gated on isUnassigned/etc. like before) —
                  every receipt is now in exactly one of four transaction-list states (Pending /
                  Linked / Added to transactions / Not shown in transactions), so there's always
                  at least one chip to show below. Address deliberately not shown here — it's
                  still editable metadata (see the Edit dialog), just not relevant enough to take
                  up space on the card itself. */}
              <Box sx={{ mb: 1, display: 'flex', gap: 0.5, flexWrap: 'wrap', alignItems: 'center' }}>
                  {(invoice.bank_transaction_id || invoice.bank_statement_transaction_id) && (
                    <>
                      <Tooltip title="Attached as proof of a bank transaction">
                        <Chip
                          icon={<LinkIcon sx={{ fontSize: '12px !important' }} />}
                          label="Linked"
                          size="small" color="success" variant="outlined"
                          sx={{ height: 20, fontSize: 10, fontWeight: 700 }}
                        />
                      </Tooltip>
                      <Button
                        size="small" variant="text" startIcon={<UnlinkIcon sx={{ fontSize: '14px !important' }} />}
                        onClick={(e) => { e.preventDefault(); e.stopPropagation(); setUnlinkProofError(null); setUnlinkProofConfirmOpen(true); }}
                        sx={{ fontSize: 11, py: 0, minWidth: 0, textTransform: 'none' }}
                      >
                        Unlink
                      </Button>
                    </>
                  )}
                  {invoice.confirmed === false && (
                    <>
                      <Tooltip title="Not counted in your totals yet">
                        <Chip
                          label="Pending"
                          size="small" color="warning" variant="outlined"
                          sx={{ height: 20, fontSize: 10, fontWeight: 700 }}
                        />
                      </Tooltip>
                      {onReview && (
                        <Button
                          size="small" variant="text" startIcon={<LinkIcon sx={{ fontSize: '14px !important' }} />}
                          onClick={(e) => { e.preventDefault(); e.stopPropagation(); onReview(invoice); }}
                          sx={{ fontSize: 11, py: 0, minWidth: 0, textTransform: 'none' }}
                        >
                          Link or confirm
                        </Button>
                      )}
                    </>
                  )}
                  {!invoice.bank_transaction_id && !invoice.bank_statement_transaction_id && invoice.confirmed !== false && invoice.excluded_from_transaction_list && (
                    <>
                      <Tooltip title="Still counted in your totals, just hidden from the bank 'View transactions' list">
                        <Chip
                          label="Not shown in transactions"
                          size="small" variant="outlined"
                          sx={{ height: 20, fontSize: 10, fontWeight: 700, color: 'text.secondary', borderColor: 'divider' }}
                        />
                      </Tooltip>
                      <Button
                        size="small" variant="text"
                        onClick={(e) => { e.preventDefault(); e.stopPropagation(); handleShowInTransactions(); }}
                        sx={{ fontSize: 11, py: 0, minWidth: 0, textTransform: 'none' }}
                      >
                        Add to transactions
                      </Button>
                    </>
                  )}
                  {!invoice.bank_transaction_id && !invoice.bank_statement_transaction_id && invoice.confirmed !== false && !invoice.excluded_from_transaction_list && (
                    <>
                      <Tooltip title="Showing as its own entry in the bank 'View transactions' list">
                        <Chip
                          label="Added to transactions"
                          size="small" color="success" variant="outlined"
                          sx={{ height: 20, fontSize: 10, fontWeight: 700 }}
                        />
                      </Tooltip>
                      <Button
                        size="small" variant="text" startIcon={<UnlinkIcon sx={{ fontSize: '14px !important' }} />}
                        onClick={(e) => { e.preventDefault(); e.stopPropagation(); handleHideFromTransactions(); }}
                        sx={{ fontSize: 11, py: 0, minWidth: 0, textTransform: 'none' }}
                      >
                        Unlink
                      </Button>
                    </>
                  )}
              </Box>

              <Divider sx={{ my: 1.25 }} />

              <Typography
                component="a"
                variant="body2"
                onClick={() => setItemsDialogOpen(true)}
                sx={{ color: 'text.secondary', cursor: 'pointer', textDecoration: 'underline', fontSize: '0.8rem' }}
              >
                View items ({items.length})
              </Typography>

              <Divider sx={{ my: 1.25 }} />

              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <Typography variant="subtitle1" sx={{ fontWeight: 700, fontSize: { xs: '0.8rem', sm: '0.85rem' } }}>Total</Typography>
                <Typography variant="h6" sx={{ fontWeight: 700, color: 'text.primary', fontSize: { xs: '1rem', sm: '1.1rem' } }}>
                  {formatCurrency(total)}
                </Typography>
              </Box>

              {itemsMismatch && (
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mt: 0.5, gap: 1 }}>
                  <Tooltip title={`Items don't add up to the total — they add up to ${formatCurrency(allItemsSum)}, but the receipt's printed total is ${formatCurrency(parseFloat(invoice.total) || 0)}. The printed total is used above — add a missing item, correct one in "View items", or scan again.`}>
                    <Box sx={{ display: 'flex', alignItems: 'center', cursor: 'help' }}>
                      <WarningIcon sx={{ fontSize: 16, color: 'warning.main' }} />
                    </Box>
                  </Tooltip>
                  <Tooltip title="Re-run extraction on the receipt photo">
                    <Button
                      size="small"
                      onClick={handleRescan}
                      disabled={rescanning}
                      startIcon={rescanning ? <CircularProgress size={12} /> : <RescanIcon sx={{ fontSize: 14 }} />}
                      sx={{ fontSize: '0.7rem', minWidth: 0, py: 0.25 }}
                    >
                      Scan again
                    </Button>
                  </Tooltip>
                </Box>
              )}
              {rescanError && (
                <Typography variant="caption" sx={{ color: 'error.main', display: 'block', mt: 0.5 }}>
                  {rescanError}
                </Typography>
              )}
            </Box>
          </Box>
        </CardContent>
      </Card>

      {/* Items dialog — toggle inclusion, total recalculates live */}
      <Dialog open={itemsDialogOpen} onClose={() => setItemsDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          Receipt items
          <IconButton size="small" onClick={() => setItemsDialogOpen(false)}><CloseIcon fontSize="small" /></IconButton>
        </DialogTitle>
        <DialogContent dividers>
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1.5 }}>
            Exclude anything that isn't relevant to this expense, or click the name/amount to fix an OCR misread — either way the total updates immediately.
          </Typography>
          <Stack spacing={0.5}>
            {items.map(item => {
              const editingDescription = editingField?.itemId === item.id && editingField.field === 'description';
              const editingCost = editingField?.itemId === item.id && editingField.field === 'cost';
              return (
                <Box
                  key={item.id}
                  sx={{
                    display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1,
                    py: 0.75, px: 1, borderRadius: 1,
                    bgcolor: item.included ? 'transparent' : 'action.hover',
                    opacity: item.included ? 1 : 0.5,
                  }}
                >
                  {editingDescription ? (
                    <TextField
                      size="small"
                      autoFocus
                      value={editFieldValue}
                      onChange={(e) => setEditFieldValue(e.target.value)}
                      onBlur={commitEditField}
                      onKeyDown={(e) => { if (e.key === 'Enter') commitEditField(); if (e.key === 'Escape') cancelEditField(); }}
                      sx={{ flex: 1, minWidth: 0 }}
                    />
                  ) : (
                    <Tooltip title="Click to edit">
                      <Typography
                        variant="body2"
                        onClick={() => startEditField(item, 'description')}
                        sx={{
                          textDecoration: item.included ? 'none' : 'line-through', flex: 1, minWidth: 0,
                          cursor: 'pointer', '&:hover': { textDecoration: 'underline' },
                        }}
                        noWrap
                      >
                        {item.description}
                      </Typography>
                    </Tooltip>
                  )}

                  {editingCost ? (
                    <TextField
                      size="small"
                      autoFocus
                      type="number"
                      value={editFieldValue}
                      onChange={(e) => setEditFieldValue(e.target.value)}
                      onBlur={commitEditField}
                      onKeyDown={(e) => { if (e.key === 'Enter') commitEditField(); if (e.key === 'Escape') cancelEditField(); }}
                      InputProps={{ startAdornment: <InputAdornment position="start">£</InputAdornment> }}
                      sx={{ width: 110 }}
                    />
                  ) : (
                    <Tooltip title="Click to edit">
                      <Typography
                        variant="body2"
                        onClick={() => startEditField(item, 'cost')}
                        sx={{
                          fontWeight: 600, textDecoration: item.included ? 'none' : 'line-through',
                          cursor: 'pointer', '&:hover': { textDecoration: 'underline' },
                        }}
                      >
                        £{parseFloat(item.cost).toFixed(2)}
                      </Typography>
                    </Tooltip>
                  )}

                  <Tooltip title={item.included ? 'Exclude from total' : 'Restore to total'}>
                    <IconButton size="small" onClick={() => handleToggleItem(item)} sx={{ color: item.included ? 'error.main' : 'success.main' }}>
                      {item.included ? <CloseIcon sx={{ fontSize: 16 }} /> : <UndoIcon sx={{ fontSize: 16 }} />}
                    </IconButton>
                  </Tooltip>
                </Box>
              );
            })}
          </Stack>

          {/* Add a missing item — e.g. one OCR skipped entirely, to reconcile a mismatch */}
          <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', mt: 1.5 }}>
            <TextField
              size="small" placeholder="Item description" value={newItemDescription}
              onChange={(e) => setNewItemDescription(e.target.value)}
              onKeyDown={(e) => { if (e.key === 'Enter') handleAddItem(); }}
              sx={{ flex: 1 }}
            />
            <TextField
              size="small" placeholder="0.00" type="number" value={newItemCost}
              onChange={(e) => setNewItemCost(e.target.value)}
              onKeyDown={(e) => { if (e.key === 'Enter') handleAddItem(); }}
              InputProps={{ startAdornment: <InputAdornment position="start">£</InputAdornment> }}
              sx={{ width: 110 }}
            />
            <IconButton
              size="small" onClick={handleAddItem}
              disabled={addingItem || !newItemDescription.trim() || !newItemCost}
              sx={{ color: 'primary.main' }}
            >
              {addingItem ? <CircularProgress size={18} /> : <AddIcon fontSize="small" />}
            </IconButton>
          </Box>

          <Divider sx={{ my: 1.5 }} />
          <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
            <Typography variant="subtitle2" fontWeight={700}>Total</Typography>
            <Typography variant="subtitle2" fontWeight={700} color="text.primary">{formatCurrency(total)}</Typography>
          </Box>
          {itemsMismatch && (
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mt: 0.75, gap: 1 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                <WarningIcon sx={{ fontSize: 14, color: 'warning.main' }} />
                <Typography variant="caption" sx={{ color: 'warning.main' }}>
                  Items sum to {formatCurrency(allItemsSum)} — receipt's printed total is used above.
                </Typography>
              </Box>
              <Button
                size="small" onClick={handleRescan} disabled={rescanning}
                startIcon={rescanning ? <CircularProgress size={12} /> : <RescanIcon sx={{ fontSize: 14 }} />}
                sx={{ fontSize: '0.7rem', minWidth: 0, py: 0.25 }}
              >
                Scan again
              </Button>
            </Box>
          )}
          {rescanError && (
            <Typography variant="caption" sx={{ color: 'error.main', display: 'block', mt: 0.5 }}>
              {rescanError}
            </Typography>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setItemsDialogOpen(false)}>Close</Button>
        </DialogActions>
      </Dialog>

      {/* Edit dialog — merchant/date + property assignment */}
      <Dialog open={editOpen} onClose={() => setEditOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Edit Receipt</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 2 }}>
            <TextField
              label="Merchant"
              value={editData.contractor_name}
              onChange={(e) => setEditData({ ...editData, contractor_name: e.target.value })}
              fullWidth
            />
            <TextField
              label="Receipt Date (YYYY-MM-DD)"
              value={editData.invoice_date}
              onChange={(e) => setEditData({ ...editData, invoice_date: e.target.value })}
              fullWidth
            />

            <Divider sx={{ my: 1 }} />
            <Typography variant="subtitle2" fontWeight={700} color="text.primary">Assign to property</Typography>
            <Typography variant="caption" color="text.secondary" sx={{ mt: -1.5 }}>
              Receipts have no property printed on them, so this needs to be set manually.
            </Typography>
            <TextField
              label="Flat Label"
              value={editData.flat_label}
              onChange={(e) => setEditData({ ...editData, flat_label: e.target.value })}
              fullWidth
              helperText={`e.g. "Flat 1", "Unit 2B", "Whole Property"`}
            />
            <TextField
              label="Property Group (top-level grouping)"
              value={editData.normalized_address}
              onChange={(e) => setEditData({ ...editData, normalized_address: e.target.value })}
              fullWidth
              helperText={`e.g. "404 Lincoln Rd" — must match an existing property to group under it`}
            />
            <TextField
              label="Flat / Full Address (sub-level)"
              value={editData.property_address}
              onChange={(e) => setEditData({ ...editData, property_address: e.target.value })}
              fullWidth
              multiline
              rows={2}
            />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditOpen(false)}>Cancel</Button>
          <Button onClick={handleSaveEdit} variant="contained">Save</Button>
        </DialogActions>
      </Dialog>

      {/* Confirm unlinking a proof-linked receipt — mirrors ProofViewDialog's own confirmation,
          since this permanently deletes the receipt (not just the link) — see handleUnlinkProof. */}
      <Dialog open={unlinkProofConfirmOpen} onClose={() => !unlinkingProof && setUnlinkProofConfirmOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle>Unlink and delete this receipt?</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary">
            This removes it from the transaction and permanently deletes the receipt itself — the photo, its details, and its line items. This can't be undone.
          </Typography>
          {unlinkProofError && (
            <Alert severity="error" sx={{ mt: 2 }} onClose={() => setUnlinkProofError(null)}>
              {unlinkProofError}
            </Alert>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setUnlinkProofConfirmOpen(false)} disabled={unlinkingProof}>Cancel</Button>
          <Button
            variant="contained" color="error" onClick={handleUnlinkProof} disabled={unlinkingProof}
            startIcon={unlinkingProof ? <CircularProgress size={16} /> : undefined}
          >
            {unlinkingProof ? 'Deleting…' : 'Unlink and delete'}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default ReceiptCard;
